export const MAX_EXPORT_EDGE_PX = 8192;
export const MAX_EXPORT_TOTAL_PIXELS = 67_108_864;

const MM_PER_INCH = 25.4;
const MIN_EFFECTIVE_DPI = 1;

const toPositiveNumber = (value, fallback) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : fallback;
};

const mmToPixels = (mm, dpi) => {
  const widthMm = toPositiveNumber(mm, 1);
  const resolvedDpi = toPositiveNumber(dpi, 600);
  return Math.max(1, Math.round((widthMm / MM_PER_INCH) * resolvedDpi));
};

const normalizeLimits = ({ maxEdgePx, maxTotalPixels }) => ({
  maxEdgePx: Math.max(1, Math.floor(toPositiveNumber(maxEdgePx, MAX_EXPORT_EDGE_PX))),
  maxTotalPixels: Math.max(1, Math.floor(toPositiveNumber(maxTotalPixels, MAX_EXPORT_TOTAL_PIXELS))),
});

const resolveClampReason = (reasons) => {
  if (!reasons.length) {
    return null;
  }
  if (reasons.length === 2) {
    return 'edge_and_total_pixel_limit';
  }
  return reasons[0];
};

export const computeCrystalLockedExportSpec = ({
  preset,
  placement,
  maxEdgePx = MAX_EXPORT_EDGE_PX,
  maxTotalPixels = MAX_EXPORT_TOTAL_PIXELS,
} = {}) => {
  const widthMm = toPositiveNumber(preset?.widthMm, toPositiveNumber(placement?.widthMm, 50));
  const heightMm = toPositiveNumber(preset?.heightMm, toPositiveNumber(placement?.heightMm, 50));
  const requestedDpi = toPositiveNumber(preset?.dpi, 600);
  const limits = normalizeLimits({ maxEdgePx, maxTotalPixels });

  const requestedWidthPx = mmToPixels(widthMm, requestedDpi);
  const requestedHeightPx = mmToPixels(heightMm, requestedDpi);

  let effectiveDpi = requestedDpi;
  let widthPx = requestedWidthPx;
  let heightPx = requestedHeightPx;
  const clampReasons = [];

  const applyScaleFactor = (factor, reason) => {
    if (!Number.isFinite(factor) || factor >= 1) {
      return;
    }
    if (reason && !clampReasons.includes(reason)) {
      clampReasons.push(reason);
    }
    effectiveDpi = Math.max(MIN_EFFECTIVE_DPI, effectiveDpi * Math.max(0.0001, factor));
    widthPx = mmToPixels(widthMm, effectiveDpi);
    heightPx = mmToPixels(heightMm, effectiveDpi);
  };

  const edgeFactor = Math.min(limits.maxEdgePx / widthPx, limits.maxEdgePx / heightPx, 1);
  applyScaleFactor(edgeFactor, 'edge_limit');

  const totalPixels = widthPx * heightPx;
  const totalFactor = totalPixels > limits.maxTotalPixels
    ? Math.sqrt(limits.maxTotalPixels / totalPixels)
    : 1;
  applyScaleFactor(totalFactor, 'total_pixel_limit');

  for (let attempt = 0; attempt < 4; attempt += 1) {
    const currentTotal = widthPx * heightPx;
    const edgeFactorLoop = Math.min(limits.maxEdgePx / widthPx, limits.maxEdgePx / heightPx, 1);
    const totalFactorLoop = currentTotal > limits.maxTotalPixels
      ? Math.sqrt(limits.maxTotalPixels / currentTotal)
      : 1;
    const loopFactor = Math.min(edgeFactorLoop, totalFactorLoop, 1);
    if (loopFactor >= 1) {
      break;
    }
    if (edgeFactorLoop < 1) {
      applyScaleFactor(loopFactor, 'edge_limit');
    } else {
      applyScaleFactor(loopFactor, 'total_pixel_limit');
    }
  }

  const stillExceeds =
    widthPx > limits.maxEdgePx ||
    heightPx > limits.maxEdgePx ||
    widthPx * heightPx > limits.maxTotalPixels;

  if (stillExceeds) {
    const hardEdgeFactor = Math.min(limits.maxEdgePx / widthPx, limits.maxEdgePx / heightPx, 1);
    const hardTotalFactor = Math.min(
      1,
      Math.sqrt(limits.maxTotalPixels / Math.max(1, widthPx * heightPx))
    );
    const hardFactor = Math.min(hardEdgeFactor, hardTotalFactor, 1);
    if (hardFactor < 1) {
      if (hardEdgeFactor < 1 && !clampReasons.includes('edge_limit')) {
        clampReasons.push('edge_limit');
      }
      if (hardTotalFactor < 1 && !clampReasons.includes('total_pixel_limit')) {
        clampReasons.push('total_pixel_limit');
      }
      effectiveDpi = Math.max(MIN_EFFECTIVE_DPI, effectiveDpi * hardFactor);
      widthPx = Math.max(1, Math.floor(widthPx * hardFactor));
      heightPx = Math.max(1, Math.floor(heightPx * hardFactor));
    }
  }

  const roundedEffectiveDpi = Math.max(
    MIN_EFFECTIVE_DPI,
    Number.isFinite(effectiveDpi) ? Number(effectiveDpi.toFixed(2)) : requestedDpi
  );

  return {
    requestedDpi,
    effectiveDpi: roundedEffectiveDpi,
    requestedWidthPx,
    requestedHeightPx,
    widthPx,
    heightPx,
    wasClamped: clampReasons.length > 0,
    clampReason: resolveClampReason(clampReasons),
  };
};

export const computeCrystalVisibleRegion = ({
  imageWidth,
  imageHeight,
  placement,
}) => {
  const safeImageWidth = Math.max(1, toPositiveNumber(imageWidth, 1));
  const safeImageHeight = Math.max(1, toPositiveNumber(imageHeight, 1));
  const boardX = Number.isFinite(Number(placement?.boardX)) ? Number(placement.boardX) : 0;
  const boardY = Number.isFinite(Number(placement?.boardY)) ? Number(placement.boardY) : 0;
  const boardWidth = Math.max(1, toPositiveNumber(placement?.boardWidth, safeImageWidth));
  const boardHeight = Math.max(1, toPositiveNumber(placement?.boardHeight, safeImageHeight));

  const boardMinX = boardX;
  const boardMinY = boardY;
  const boardMaxX = boardX + boardWidth;
  const boardMaxY = boardY + boardHeight;
  const visibleMinX = Math.max(0, boardMinX);
  const visibleMinY = Math.max(0, boardMinY);
  const visibleMaxX = Math.min(safeImageWidth, boardMaxX);
  const visibleMaxY = Math.min(safeImageHeight, boardMaxY);
  const hasIntersection = visibleMaxX > visibleMinX && visibleMaxY > visibleMinY;

  if (!hasIntersection) {
    return {
      x: 0,
      y: 0,
      width: safeImageWidth,
      height: safeImageHeight,
    };
  }

  return {
    x: visibleMinX,
    y: visibleMinY,
    width: visibleMaxX - visibleMinX,
    height: visibleMaxY - visibleMinY,
  };
};

export const computeVisibleRegionMm = ({
  visibleRegion,
  placement,
}) => {
  const boardWidth = Math.max(1, toPositiveNumber(placement?.boardWidth, 1));
  const boardHeight = Math.max(1, toPositiveNumber(placement?.boardHeight, 1));
  const widthMm = Math.max(0.01, toPositiveNumber(placement?.widthMm, 1));
  const heightMm = Math.max(0.01, toPositiveNumber(placement?.heightMm, 1));
  const visibleWidth = Math.max(0.01, toPositiveNumber(visibleRegion?.width, 1));
  const visibleHeight = Math.max(0.01, toPositiveNumber(visibleRegion?.height, 1));

  return {
    widthMm: (visibleWidth / boardWidth) * widthMm,
    heightMm: (visibleHeight / boardHeight) * heightMm,
  };
};

export const computeCrystalBoardDrawTransform = ({
  sourceWidth,
  sourceHeight,
  placement,
  targetWidth,
  targetHeight,
}) => {
  const boardWidth = Math.max(1, toPositiveNumber(placement?.boardWidth, sourceWidth || 1));
  const boardHeight = Math.max(1, toPositiveNumber(placement?.boardHeight, sourceHeight || 1));
  const boardX = Number.isFinite(Number(placement?.boardX)) ? Number(placement.boardX) : 0;
  const boardY = Number.isFinite(Number(placement?.boardY)) ? Number(placement.boardY) : 0;
  const safeSourceWidth = Math.max(1, toPositiveNumber(sourceWidth, 1));
  const safeSourceHeight = Math.max(1, toPositiveNumber(sourceHeight, 1));
  const safeTargetWidth = Math.max(1, toPositiveNumber(targetWidth, 1));
  const safeTargetHeight = Math.max(1, toPositiveNumber(targetHeight, 1));
  const scaleX = safeTargetWidth / boardWidth;
  const scaleY = safeTargetHeight / boardHeight;

  return {
    dx: -boardX * scaleX,
    dy: -boardY * scaleY,
    dw: safeSourceWidth * scaleX,
    dh: safeSourceHeight * scaleY,
    scaleX,
    scaleY,
  };
};

const twodCrystalExportSizing = {
  MAX_EXPORT_EDGE_PX,
  MAX_EXPORT_TOTAL_PIXELS,
  computeCrystalLockedExportSpec,
  computeCrystalVisibleRegion,
  computeVisibleRegionMm,
  computeCrystalBoardDrawTransform,
};

export default twodCrystalExportSizing;
